/**
 * AudioStorageService — offline storage for downloaded Quran audio.
 *
 * Architecture:
 *   - Cache Storage API holds the actual MP3 blobs on web/PWA (best
 *     browser support for opaque media, evictable but persistent enough
 *     for this use case).
 *   - On native (Capacitor Android/iOS) the blobs are written to the
 *     ``Directory.Data`` private container via ``nativeFileStorage`` so
 *     they survive app/device restarts and OS storage pressure (the
 *     WebView Cache Storage is unreliable, especially on iOS WKWebView).
 *   - IndexedDB holds the metadata index in both cases: which ayahs are
 *     downloaded (with ``localUri`` on native, ``remoteUrl`` cache key
 *     on web) and which "scopes" (surah / hizb / full_quran) the user
 *     has downloaded.
 *
 * All reads/writes are isolated per (reciterId, riwaya).
 */

import { nativeFileStorage, isNative } from './nativeFileStorage';

const DB_NAME = 'noor_audio_db';
const DB_VERSION = 1;
const FILES_STORE = 'audio_files';     // key = audioFileId
const SCOPES_STORE = 'scopes';         // key = scopeId
const CACHE_NAME = 'noor-quran-audio-v1';

const isBrowser = typeof window !== 'undefined' && typeof indexedDB !== 'undefined';

/* ---------------- IndexedDB helpers ---------------- */
function openDB() {
  return new Promise((resolve, reject) => {
    if (!isBrowser) return reject(new Error('IndexedDB unavailable'));
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(FILES_STORE)) {
        const s = db.createObjectStore(FILES_STORE, { keyPath: 'id' });
        s.createIndex('by_reciter', ['reciterId', 'riwaya']);
        s.createIndex('by_ayah', ['ayahKey', 'reciterId', 'riwaya']);
      }
      if (!db.objectStoreNames.contains(SCOPES_STORE)) {
        const s = db.createObjectStore(SCOPES_STORE, { keyPath: 'id' });
        s.createIndex('by_reciter', ['reciterId', 'riwaya']);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function txStore(store, mode = 'readonly') {
  const db = await openDB();
  return db.transaction(store, mode).objectStore(store);
}

function promisify(req) {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function fileId(ayahKey, reciterId, riwaya) {
  return `${reciterId}::${riwaya}::${ayahKey}`;
}

function scopeId({ scopeType, reciterId, riwaya, surahNumber, hizbNumber }) {
  if (scopeType === 'surah') return `surah-${surahNumber}::${reciterId}::${riwaya}`;
  if (scopeType === 'hizb')  return `hizb-${hizbNumber}::${reciterId}::${riwaya}`;
  return `full::${reciterId}::${riwaya}`;
}

async function getCache() {
  if (!('caches' in window)) throw new Error('Cache Storage unavailable');
  return caches.open(CACHE_NAME);
}

/* ---------------- Public API ---------------- */
export const audioStorage = {
  isAvailable() {
    // Always available in either mode (Cache Storage on web, Filesystem on native).
    return isBrowser && (isNative() || 'caches' in window);
  },

  /** Persist a downloaded mp3 blob and update the index. */
  async saveAudioFile(file, blob) {
    if (!this.isAvailable()) throw new Error('Audio storage unavailable');

    let localRelPath;
    if (isNative()) {
      // Write the file to the app's private container (Directory.Data).
      const written = await nativeFileStorage.writeAudio(file, blob);
      localRelPath = written?.relPath || null;
    } else {
      // Web/PWA: use Cache Storage with the remoteUrl as the canonical key.
      const cache = await getCache();
      await cache.put(new Request(file.remoteUrl), new Response(blob, {
        headers: { 'Content-Type': 'audio/mpeg', 'X-Noor-Cached-At': new Date().toISOString() },
      }));
    }

    const store = await txStore(FILES_STORE, 'readwrite');
    await promisify(store.put({
      id: fileId(file.ayahKey, file.reciterId, file.riwaya),
      ayahKey: file.ayahKey,
      surahNumber: file.surahNumber,
      ayahNumber: file.ayahNumber,
      globalAyahNumber: file.globalAyahNumber,
      hizbNumber: file.hizbNumber,
      reciterId: file.reciterId,
      riwaya: file.riwaya,
      remoteUrl: file.remoteUrl,
      localRelPath: localRelPath || null,
      storageType: isNative() ? 'capacitor_filesystem' : 'cache_storage',
      sizeBytes: blob.size,
      downloadedAt: new Date().toISOString(),
    }));
  },

  /** Returns a URL playable by <audio>, or null if not cached. */
  async getLocalAudioUrl(ayahKey, reciterId, riwaya) {
    if (!this.isAvailable()) return null;
    const store = await txStore(FILES_STORE);
    const rec = await promisify(store.get(fileId(ayahKey, reciterId, riwaya)));
    if (!rec) return null;

    if (isNative() && rec.localRelPath) {
      const url = await nativeFileStorage.getPlayableUrl(rec.localRelPath);
      if (url) return url;
      // File missing on disk → drop stale metadata so caller can re-download.
      try {
        const w = await txStore(FILES_STORE, 'readwrite');
        await promisify(w.delete(rec.id));
      } catch { /* ignore */ }
      return null;
    }

    // Web/PWA path
    if (!('caches' in window)) return null;
    try {
      const cache = await getCache();
      const resp = await cache.match(new Request(rec.remoteUrl));
      if (!resp) return null;
      const blob = await resp.blob();
      return URL.createObjectURL(blob);
    } catch {
      return null;
    }
  },

  async isAyahDownloaded(ayahKey, reciterId, riwaya) {
    if (!this.isAvailable()) return false;
    const store = await txStore(FILES_STORE);
    const rec = await promisify(store.get(fileId(ayahKey, reciterId, riwaya)));
    if (!rec) return false;
    // On native, double-check the file is still on disk (defensive).
    if (isNative() && rec.localRelPath) {
      const ok = await nativeFileStorage.exists(rec.localRelPath);
      if (!ok) {
        try {
          const w = await txStore(FILES_STORE, 'readwrite');
          await promisify(w.delete(rec.id));
        } catch { /* ignore */ }
        return false;
      }
    }
    return true;
  },

  async upsertDownloadedScope(scope) {
    const id = scopeId(scope);
    const store = await txStore(SCOPES_STORE, 'readwrite');
    const existing = await promisify(store.get(id));
    const merged = { ...(existing || {}), ...scope, id };
    await promisify(store.put(merged));
    return merged;
  },

  async getDownloadedScope(id) {
    const store = await txStore(SCOPES_STORE);
    return promisify(store.get(id));
  },

  async getDownloadedScopes() {
    const store = await txStore(SCOPES_STORE);
    return promisify(store.getAll());
  },

  async isScopeDownloaded(scope) {
    const id = scopeId(scope);
    const rec = await this.getDownloadedScope(id);
    return !!(rec && rec.status === 'completed');
  },

  /** Remove a scope record + the audio files it owns (if not shared). */
  async deleteScope(scopeIdStr) {
    if (!scopeIdStr) throw new Error('Scope ID manquant');
    const sStore = await txStore(SCOPES_STORE, 'readwrite');
    const rec = await promisify(sStore.get(scopeIdStr));
    if (!rec) {
      console.warn(`Scope introuvable: ${scopeIdStr} — peut-être déjà supprimée`);
      return; // Silent return for already-deleted scopes
    }
    // Delete audio files belonging to this scope
    const fStore = await txStore(FILES_STORE, 'readwrite');
    const cache = isNative() ? null : (('caches' in window) ? await getCache() : null);
    for (const ayahKey of rec.ayahKeys || []) {
      const fid = fileId(ayahKey, rec.reciterId, rec.riwaya);
      const fileRec = await promisify(fStore.get(fid));
      if (fileRec) {
        await promisify(fStore.delete(fid));
        if (isNative() && fileRec.localRelPath) {
          try { await nativeFileStorage.deleteAudio(fileRec.localRelPath); } catch { /* ignore */ }
        } else if (cache) {
          try { await cache.delete(new Request(fileRec.remoteUrl)); } catch { /* ignore */ }
        }
      }
    }
    await promisify(sStore.delete(scopeIdStr));
  },

  async deleteSurah(surahNumber, reciterId, riwaya) {
    return this.deleteScope(scopeId({ scopeType: 'surah', surahNumber, reciterId, riwaya }));
  },

  async deleteHizb(hizbNumber, reciterId, riwaya) {
    return this.deleteScope(scopeId({ scopeType: 'hizb', hizbNumber, reciterId, riwaya }));
  },

  async deleteFullQuran(reciterId, riwaya) {
    return this.deleteScope(scopeId({ scopeType: 'full_quran', reciterId, riwaya }));
  },

  async getStorageUsage() {
    if (isNative()) {
      const used = await nativeFileStorage.estimateUsageBytes();
      return { usedBytes: used, availableBytes: undefined };
    }
    if (!('storage' in navigator) || !navigator.storage.estimate) {
      return { usedBytes: undefined, availableBytes: undefined };
    }
    const est = await navigator.storage.estimate();
    return {
      usedBytes: est.usage,
      availableBytes: est.quota ? Math.max(0, est.quota - (est.usage || 0)) : undefined,
    };
  },

  // ---- low-level helpers used by the download service ----
  _scopeId: scopeId,
  _fileId: fileId,
};
